import { HttpService } from "../../modules/shared/services/http.service";
import { 
  UserPermission, 
  UserPermissionCreateDto, 
  UserPermissionUpdateDto,
  UserPermissionResponse,
  UserPermissionQuery
} from "../../modules/shared/model/user-permissions.interfaces";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

// Get all user permissions with pagination and filtering
export const getUserPermissions = async (query?: UserPermissionQuery): Promise<UserPermissionResponse> => {
  const qs = new URLSearchParams();
  
  if (query?.page) qs.append("page", String(query.page));
  if (query?.limit) qs.append("limit", String(query.limit));
  
  if (query?.filters) {
    if (query.filters.role) qs.append("filters", JSON.stringify({ role: query.filters.role }));
    if (query.filters.status) qs.append("filters", JSON.stringify({ status: query.filters.status }));
    if (query.filters.permissionType) qs.append("filters", JSON.stringify({ permissionType: query.filters.permissionType }));
    if (query.filters.search) qs.append("filters", JSON.stringify({ search: query.filters.search }));
  }
  
  if (query?.sort) {
    // Format sort parameter as expected by the API: [{"order":"createdAt","orderBy":"desc"}]
    const sortArray = [{
      order: query.sort.field,
      orderBy: query.sort.direction
    }];
    qs.append("sort", JSON.stringify(sortArray));
  }
  
  const response = await HttpService.get(`api/v1/user-permissions${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

// Get user permission by ID
export const getUserPermissionById = async (id: string): Promise<UserPermission> => {
  const response = await HttpService.get(`api/v1/user-permissions/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// Create new user permission
export const createUserPermission = async (data: UserPermissionCreateDto): Promise<UserPermission> => {
  const response = await HttpService.post("api/v1/user-permissions", data, headers, true);
  return response;
};

// Update user permission
export const updateUserPermission = async (id: string, data: UserPermissionUpdateDto): Promise<UserPermission> => {
  const response = await HttpService.patch(`api/v1/user-permissions/${id}`, data, headers, true);
  return response;
};

// Delete user permission
export const deleteUserPermission = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/user-permissions/${id}`, { "x-custom-lang": "en" }, true);
};

// Get users for dropdown (this would typically be a separate endpoint)
export const getUsersForPermissions = async (): Promise<any[]> => {
  try {
    const response = await HttpService.get("api/v1/users", true, { "x-custom-lang": "en" });
    // Handle different possible response structures
    const users = response.data || response || [];
    
    // Ensure each user has the required fields and convert objects to strings
    return users.map((user: any) => ({
      id: user.id || "",
      name: user.name || user.firstName || "",
      email: user.email || "",
      role: user.role || user.userType || "client",
      category: user.category || user.profession || "",
      status: user.status || "active"
    }));
  } catch (error) {
    console.error("Failed to fetch users:", error);
    return [];
  }
};
